import { useState } from 'react';
import { Alert, Badge, Box, Button, Heading, Spinner, Table, TextInput } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const size = (bytes) =>
  bytes >= 1 << 20 ? `${(bytes / (1 << 20)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * The public trust portal's administration: who asked to be let in, which
 * organizations are, the documents they may download, and every download.
 *
 * Editors only, like the gate and the access list: who Hyphen's customers are,
 * and what they took, is how Hyphen runs the service.
 */
export function PortalAdmin({ canEdit }) {
  return (
    <Box gap="4xl" maxWidth="64rem">
      <Box gap="sm">
        <Heading as="h1" size="lg">Portal</Heading>
        <Box as="p" color="secondary">
          The public trust portal at the site's root. Customers sign in with their Hyphen account, and may
          download the documents when one of their Hyphen organizations has access.
        </Box>
      </Box>
      <Requests canEdit={canEdit} />
      <Grants canEdit={canEdit} />
      <Documents canEdit={canEdit} />
      <Downloads />
    </Box>
  );
}

/** Run one action at a time, keeping its error, and reload after it. */
function useAction(reload) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  async function act(key, fn) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await reload();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(null);
    }
  }
  return { busy, error, act };
}

function Section({ title, help, children }) {
  return (
    <Box gap="xl">
      <Box gap="xs">
        <Heading as="h2" size="md">{title}</Heading>
        {help && <Box as="p" fontSize="sm" color="secondary">{help}</Box>}
      </Box>
      {children}
    </Box>
  );
}

function Requests({ canEdit }) {
  const { data: requests, error, reload } = useResource('/access-requests');
  const action = useAction(reload);

  const columns = [
    {
      heading: 'Organization',
      dataKey: 'organization_name',
      render: (_cell, row) => (
        <Box gap="2xs">
          <span>{row.organization_name}</span>
          <Box as="span" fontSize="xs" color="secondary">{row.organization_id}</Box>
        </Box>
      ),
    },
    { heading: 'Asked by', dataKey: 'requested_by' },
    { heading: 'Asked', dataKey: 'requested_at', render: (cell) => when(cell) },
    {
      heading: '',
      dataKey: 'actions',
      width: 220,
      render: (_cell, row) => {
        if (row.status !== 'pending') {
          return (
            <Box gap="2xs">
              <Badge variant="outline">{row.status}</Badge>
              <Box as="span" fontSize="xs" color="secondary">{row.decided_by}</Box>
            </Box>
          );
        }
        if (!canEdit) return <Badge variant="outline">pending</Badge>;
        return (
          <Box direction="row" gap="sm">
            <Button size="sm" isLoading={action.busy === `approve:${row.id}`} isDisabled={Boolean(action.busy)}
                    onClick={() => action.act(`approve:${row.id}`, () => api.post(`/access-requests/${row.id}/approve`))}>
              Approve
            </Button>
            <Button size="sm" variant="secondary" isLoading={action.busy === `decline:${row.id}`}
                    isDisabled={Boolean(action.busy)}
                    onClick={() => action.act(`decline:${row.id}`, () => api.post(`/access-requests/${row.id}/decline`))}>
              Decline
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Section title="Access requests" help="Approving one gives the organization access.">
      {(error || action.error) && <Alert variant="warning">{error || action.error}</Alert>}
      {!requests && !error && <Spinner />}
      {requests && requests.length === 0 && <Box color="secondary">Nobody has asked for access yet.</Box>}
      {requests && requests.length > 0 && <Table rowKey="id" columns={columns} rows={requests} />}
    </Section>
  );
}

function Grants({ canEdit }) {
  const { data: grants, error, reload } = useResource('/grants');
  const action = useAction(reload);
  const [organizationID, setOrganizationID] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  async function grant(event) {
    event.preventDefault();
    const granted = await action.act('grant', () =>
      api.post('/grants', { organization_id: organizationID.trim(), organization_name: organizationName.trim() }));
    // Cleared only once it worked, so a refusal leaves what was typed.
    if (granted) {
      setOrganizationID('');
      setOrganizationName('');
    }
  }

  const columns = [
    {
      heading: 'Organization',
      dataKey: 'organization_name',
      render: (_cell, row) => (
        <Box gap="2xs">
          <span>{row.organization_name}</span>
          <Box as="span" fontSize="xs" color="secondary">{row.organization_id}</Box>
        </Box>
      ),
    },
    {
      heading: 'Given',
      dataKey: 'granted_at',
      render: (_cell, row) => (
        <Box gap="2xs">
          <span>{when(row.granted_at)}</span>
          <Box as="span" fontSize="xs" color="secondary">
            {row.granted_by}{row.request_id ? ', on request' : ''}
          </Box>
        </Box>
      ),
    },
    {
      heading: '',
      dataKey: 'actions',
      width: 200,
      render: (_cell, row) => {
        if (!row.active) {
          return (
            <Box gap="2xs">
              <Badge variant="outline">revoked</Badge>
              <Box as="span" fontSize="xs" color="secondary">{row.revoked_by}, {when(row.revoked_at)}</Box>
            </Box>
          );
        }
        if (!canEdit) return null;
        return (
          <Button size="sm" variant="danger" isLoading={action.busy === `revoke:${row.id}`}
                  isDisabled={Boolean(action.busy)}
                  onClick={() => action.act(`revoke:${row.id}`, () => api.delete(`/grants/${row.id}`))}>
            Revoke
          </Button>
        );
      },
    },
  ];

  return (
    <Section
      title="Organizations with access"
      help="Members of these Hyphen organizations may download every document. An organization's ID starts with org_ and is the first part of its address in the Hyphen app: app.hyphen.ai/org_…"
    >
      {(error || action.error) && <Alert variant="warning">{error || action.error}</Alert>}
      {!grants && !error && <Spinner />}
      {grants && grants.length === 0 && <Box color="secondary">No organization has access yet.</Box>}
      {grants && grants.length > 0 && <Table rowKey="id" columns={columns} rows={grants} />}
      {canEdit && (
        <Box as="form" direction="row" gap="lg" alignItems="flex-end" onSubmit={grant}>
          <Box flex="auto">
            <TextInput id="grant-organization-id" label="Organization ID" value={organizationID}
                       onChange={(event) => setOrganizationID(event.target.value)} />
          </Box>
          <Box flex="auto">
            <TextInput id="grant-organization-name" label="Name" value={organizationName}
                       onChange={(event) => setOrganizationName(event.target.value)} />
          </Box>
          <Button type="submit" isLoading={action.busy === 'grant'}
                  isDisabled={Boolean(action.busy) || !organizationID.trim() || !organizationName.trim()}>
            Give access
          </Button>
        </Box>
      )}
    </Section>
  );
}

function Documents({ canEdit }) {
  const { data: documents, error, reload } = useResource('/documents');
  const action = useAction(reload);
  const [title, setTitle] = useState('');
  const [section, setSection] = useState('');

  async function create(event) {
    event.preventDefault();
    const created = await action.act('create', () =>
      api.post('/documents', { title: title.trim(), section: section.trim() }));
    if (created) {
      setTitle('');
      setSection('');
    }
  }

  const current = (documents ?? []).filter((d) => !d.retired);
  const retired = (documents ?? []).filter((d) => d.retired);

  return (
    <Section
      title="Documents"
      help="Customers get the newest file uploaded for each. Earlier files are kept, so every download can be traced to the exact file handed out."
    >
      {(error || action.error) && <Alert variant="warning">{error || action.error}</Alert>}
      {!documents && !error && <Spinner />}
      {documents && current.length === 0 && <Box color="secondary">No documents yet.</Box>}
      {current.map((document) => (
        <DocumentRow key={document.id} document={document} canEdit={canEdit} action={action} />
      ))}
      {canEdit && (
        <Box as="form" direction="row" gap="lg" alignItems="flex-end" onSubmit={create}>
          <Box flex="auto">
            <TextInput id="document-title" label="Title" value={title}
                       placeholder="SOC 2 Type 1 report"
                       onChange={(event) => setTitle(event.target.value)} />
          </Box>
          <Box flex="auto">
            <TextInput id="document-section" label="Listed under" value={section}
                       placeholder="Compliance reports"
                       onChange={(event) => setSection(event.target.value)} />
          </Box>
          <Button type="submit" isLoading={action.busy === 'create'}
                  isDisabled={Boolean(action.busy) || !title.trim() || !section.trim()}>
            Add document
          </Button>
        </Box>
      )}
      {retired.length > 0 && (
        <Box gap="xs">
          <Box as="span" fontSize="sm" fontWeight="medium" color="secondary">Retired</Box>
          {retired.map((d) => (
            <Box key={d.id} as="span" fontSize="sm" color="secondary">
              {d.title} — retired by {d.retired_by}, {when(d.retired_at)}
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
}

function DocumentRow({ document, canEdit, action }) {
  const [file, setFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // Bumped to clear the file input once an upload lands; a file input cannot be
  // cleared by setting its value from React.
  const [inputKey, setInputKey] = useState(0);
  const latest = document.latest;

  async function upload() {
    const uploaded = await action.act(`upload:${document.id}`, () => api.upload(`/documents/${document.id}/versions`, file));
    if (uploaded) {
      setFile(null);
      setInputKey((n) => n + 1);
    }
  }

  return (
    <Box padding="xl" borderWidth="sm" borderColor="subtle" radius="md" gap="md">
      <Box direction="row" alignItems="center" gap="md">
        <Box gap="2xs" flex="auto">
          <Box as="span" fontWeight="medium">{document.title}</Box>
          <Box as="span" fontSize="sm" color="secondary">Listed under {document.section}</Box>
        </Box>
        {!latest && <Badge variant="outline">No file — not shown to customers</Badge>}
      </Box>
      {latest && (
        <Box as="span" fontSize="sm" color="secondary">
          {latest.filename}, {size(latest.size_bytes)}, uploaded by {latest.uploaded_by} {when(latest.uploaded_at)}
        </Box>
      )}
      {canEdit && (
        <Box direction="row" alignItems="center" gap="md">
          {/*
            Uploaded when the button is pressed, never on choosing the file: a
            file picked by mistake should not reach customers before anyone has
            looked at what was picked.
          */}
          <input key={inputKey} type="file" aria-label={`File for ${document.title}`}
                 onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
          {file && (
            <Button size="sm" isLoading={action.busy === `upload:${document.id}`} isDisabled={Boolean(action.busy)}
                    onClick={upload}>
              {latest ? 'Upload as the new version' : 'Upload'}
            </Button>
          )}
          <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm" alignItems="center">
            {!confirming && (
              <Button size="sm" variant="tertiary" isDisabled={Boolean(action.busy)} onClick={() => setConfirming(true)}>
                Retire
              </Button>
            )}
            {confirming && (
              <>
                <Box as="span" fontSize="sm">Stop offering it? Its download record stays.</Box>
                <Button size="sm" variant="danger" isLoading={action.busy === `retire:${document.id}`}
                        onClick={() => action.act(`retire:${document.id}`, () => api.delete(`/documents/${document.id}`))}>
                  Retire
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>Cancel</Button>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Downloads() {
  const { data: downloads, error } = useResource('/downloads');

  const columns = [
    { heading: 'When', dataKey: 'downloaded_at', render: (cell) => when(cell) },
    { heading: 'Who', dataKey: 'email' },
    { heading: 'Organization', dataKey: 'organization_name' },
    {
      heading: 'Document',
      dataKey: 'document_title',
      render: (_cell, row) => (
        <Box gap="2xs">
          <span>{row.document_title}</span>
          <Box as="span" fontSize="xs" color="secondary" title={row.sha256}>
            {row.filename}, sha256 {row.sha256.slice(0, 12)}
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Section title="Downloads" help="Every document handed out, newest first.">
      {error && <Alert variant="warning">{error}</Alert>}
      {!downloads && !error && <Spinner />}
      {downloads && downloads.length === 0 && <Box color="secondary">Nothing has been downloaded yet.</Box>}
      {downloads && downloads.length > 0 && <Table rowKey="id" columns={columns} rows={downloads} />}
    </Section>
  );
}
